import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import CapabilityPicker from '@/frontend/features/LocalMcp/CapabilityPicker'
import OrganizationPicker from '@/frontend/features/LocalMcp/OrganizationPicker'
import WorkflowPermissionPicker from '@/frontend/features/LocalMcp/WorkflowPermissionPicker'
import { formatDate, mcpPath, mcpUrl, type AuthorizationValues, type LocalMcpProps } from '@/frontend/features/LocalMcp/types'

type Props = LocalMcpProps & { onCancel: () => void; onCreated: () => void; onProcessingChange: (processing: boolean) => void }
export default function CredentialForm(props: Props) {
  const { copy, editing_access: access, authorization_options: options } = props
  const legacy = !!access && access.scope_kind !== 'engineering'
  const serverErrors = usePage().props.errors as Record<string, string>
  const form = useForm<AuthorizationValues>({ name: access?.name || '', environment: access?.environment || 'local',
    organization_ids: access?.organization_ids || [], all_organizations: access?.all_organizations || false,
    capabilities: access?.capabilities || [], all_capabilities: access?.all_capabilities || false,
    validity: access ? '' : 'one_month', lock_version: access?.lock_version || 0,
    praxis_workflow_permissions: access ? access.praxis_workflow_permissions : {} })
  const onChange = (values: Partial<AuthorizationValues>) => {
    const next = { ...form.data, ...values }
    if (next.praxis_workflow_permissions !== null && !next.all_organizations) {
      next.praxis_workflow_permissions = Object.fromEntries(Object.entries(next.praxis_workflow_permissions).filter(([id]) => next.organization_ids.includes(id)))
    }
    form.setData(next)
  }
  const submit = () => {
    form.transform((data) => {
      const attributes: Record<string, unknown> = { ...data }
      if (!attributes.validity) delete attributes.validity
      if (access) {
        delete attributes.environment
        const sameActions = JSON.stringify([...data.capabilities].sort()) === JSON.stringify([...access.capabilities].sort())
        if (sameActions && data.all_capabilities === access.all_capabilities) {
          delete attributes.capabilities
          delete attributes.all_capabilities
        }
      }
      if (legacy) { delete attributes.organization_ids; delete attributes.all_organizations }
      return { mcp_access: attributes }
    })
    const behavior = { preserveScroll: true, onSuccess: props.onCreated,
      onStart: () => props.onProcessingChange(true), onFinish: () => props.onProcessingChange(false) }
    if (access) form.patch(mcpUrl(`${mcpPath}/${access.id}`, props.filters, props.pagination.page), behavior)
    else form.post(mcpUrl(mcpPath, props.filters, props.pagination.page), behavior)
  }
  const disabled = form.processing || (!access && !options.engineering_enabled)
  return <form className="space-y-6 rounded-lg border border-border bg-card p-5" onSubmit={(event) => { event.preventDefault(); submit() }}>
    <header>
      <h2 className="text-base font-semibold">{access ? copy.edit_connection : copy.new_connection}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{copy.authorization}</p>
    </header>
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="mcp-access-name">{copy.name}</Label>
        <Input id="mcp-access-name" value={form.data.name} onChange={(event) => onChange({ name: event.target.value })} maxLength={100} required />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="mcp-access-environment">{copy.environment}</Label>
        {access ? <Input id="mcp-access-environment" className="bg-muted" value={copy[`environment_${access.environment}`] || copy.legacy_environment} readOnly />
          : <select id="mcp-access-environment" value={form.data.environment} onChange={(event) => onChange({ environment: event.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
            {options.environments.map((value) => <option key={value} value={value}>{copy[`environment_${value}`] || value}</option>)}
          </select>}
        {access && <p className="text-xs text-muted-foreground">{copy.environment_fixed}</p>}
      </div>
    </div>
    {legacy ? <section className="rounded-md bg-muted p-3 text-sm">
      <h3 className="font-medium">{copy.fixed_scope}</h3>
      <p>{access.organizations.map((organization) => organization.name).join(', ')}{access.project_name ? ` · ${access.project_name}` : ''}</p>
      <p className="mt-1 text-xs text-muted-foreground">{copy.legacy_scope_hint}</p>
    </section> : <OrganizationPicker options={props.organization_options} selected={access?.organizations || []} values={form.data} copy={copy} filters={props.filters} listPage={props.pagination.page} disabled={form.processing} onChange={onChange} />}
    <CapabilityPicker options={options} values={form.data} copy={copy} onChange={onChange} />
    <WorkflowPermissionPicker {...props} values={form.data} disabled={form.processing} onChange={onChange} />
    <div className="space-y-1.5 border-t border-border pt-4">
      <Label htmlFor="mcp-access-validity">{copy.validity}</Label>
      <select id="mcp-access-validity" value={form.data.validity} onChange={(event) => onChange({ validity: event.target.value })} className="block rounded-md border border-border bg-background px-3 py-2 text-sm">
        {access && <option value="">{copy.keep_validity}</option>}
        <option value="one_month">{copy.one_month}</option><option value="three_months">{copy.three_months}</option><option value="never">{copy.never}</option>
      </select>
      {access && <p className="text-xs text-muted-foreground">{access.expires_at ? `${copy.expires} ${formatDate(access.expires_at)}` : copy.no_expiration}</p>}
      <p className="text-xs text-muted-foreground">{access ? copy.edit_hint : copy.validity_hint}</p>
    </div>
    <p className="text-xs text-muted-foreground" aria-live="polite">{copy.summary}: {legacy ? copy.fixed_scope : form.data.all_organizations ? copy.all_organizations : `${copy.selected_organizations}: ${form.data.organization_ids.length}`} · {form.data.all_capabilities ? copy.all_permissions : `${copy.selected_permissions}: ${form.data.capabilities.length}`}{form.data.praxis_workflow_permissions !== null && ` · ${copy.selected_workflows}: ${Object.values(form.data.praxis_workflow_permissions).flat().length}`}</p>
    {Object.entries({ ...serverErrors, ...form.errors }).map(([key, error]) => <p key={key} role="alert" className="text-sm text-destructive">{error}</p>)}
    {!access && !options.engineering_enabled && <p role="alert" className="text-sm text-destructive">{copy.engineering_disabled}</p>}
    <div className="flex justify-end gap-2">
      {access && !form.processing ? <Button asChild variant="ghost"><Link href={mcpUrl(mcpPath, props.filters, props.pagination.page)}>{copy.cancel}</Link></Button>
        : <Button type="button" variant="ghost" disabled={form.processing} onClick={props.onCancel}>{copy.cancel}</Button>}
      <Button type="submit" disabled={disabled}>{form.processing ? copy.saving : access ? copy.save : copy.create}</Button>
    </div>
  </form>
}
